import React, { useState, useEffect, useCallback, useRef } from "react";
import Footer from "../layouts/Footer";
import {
  Button,
  Card,
  Row,
  Col,
  ProgressBar,
  Alert,
  Spinner,
  Badge
} from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import "remixicon/fonts/remixicon.css";
import { useAuth } from "../hooks/useAuth";

import {
  fetchTransactionsList,
  fetchSenderList,
  fetchPricingList,
  fetchSMSList,
  API_BASE_URL
} from "../services/api";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

import axios from 'axios';

// ============================================
// API CLIENT
// ============================================
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// ============================================
// COMPOSANT
// ============================================
export default function WebsiteAnalytics() {
  const { isAuthenticated, user, loading: authLoading, logout } = useAuth();

  // ✅ Ref pour empêcher le double fetch (StrictMode / re-render)
  const initDoneRef = useRef(false);

  const [viewMode, setViewMode] = useState("jour");
  const [solde, setSolde] = useState(null);
  const [soldeV1, setSoldeV1] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [stats, setStats] = useState({ daily: [], monthly: [] });
  const [transactions, setTransactions] = useState([]);
  const [senders, setSenders] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [recentSMS, setRecentSMS] = useState([]);

  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [sendersLoading, setSendersLoading] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);

  const [error, setError] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  // Redirection si non authentifié
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setRedirecting(true);
      window.location.href = '/login';
    }
  }, [isAuthenticated, authLoading]);

  // ============================================
  // RÉCUPÉRATION DU SOLDE
  // ============================================
  const fetchSolde = useCallback(async () => {
    if (!isAuthenticated) {
      setError("Vous devez être connecté");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get('/sms-balance');

      const balanceV1 = response.data.balance_v1 && response.data.balance_v1.length > 0
        ? response.data.balance_v1[0]
        : null;

      const balance = response.data.balance;

      if (balanceV1) {
        setSolde(parseFloat(balanceV1.solde) || 0);
        if (balanceV1.last_update) setLastUpdate(balanceV1.last_update);
      } else {
        setSolde(0);
      }

      if (balance) {
        setSoldeV1(parseFloat(balance.solde_v1) || 0);
        if (balance.derniere_maj) setLastUpdate(balance.derniere_maj);
      } else {
        setSoldeV1(0);
      }

    } catch (err) {
      console.error("❌ Erreur solde:", err);
      if (err.response?.status === 401) {
        setError("Session expirée");
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      } else {
        setSolde(0);
        setSoldeV1(0);
        setError("Impossible de récupérer le solde");
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // ============================================
  // RÉCUPÉRATION DES STATS
  // ============================================
  const fetchStats = useCallback(async () => {
    if (!isAuthenticated) {
      setStatsError("Vous devez être connecté");
      setStatsLoading(false);
      return;
    }

    try {
      setStatsLoading(true);
      setStatsError(null);

      try {
        const response = await apiClient.get('/bo-stats');
        let dailyStats = response.data.daily || response.data.data?.daily || [];
        let monthlyStats = response.data.monthly || response.data.data?.monthly || [];
        setStats({ daily: dailyStats, monthly: monthlyStats });
      } catch (firstError) {
        try {
          const response = await apiClient.get('/sms/statistics');
          const statsData = response.data.stats || response.data.data || response.data;
          setStats({
            daily: statsData.daily || [],
            monthly: statsData.monthly || []
          });
        } catch (secondError) {
          console.error("❌ Erreur statistiques:", secondError);
          setStats({ daily: [], monthly: [] });
          setStatsError("Aucune statistique disponible");
        }
      }

    } catch (err) {
      console.error("❌ Erreur statistiques:", err);
      if (err.response?.status === 401) {
        setStatsError("Session expirée");
      } else {
        setStats({ daily: [], monthly: [] });
        setStatsError("Aucune statistique disponible");
      }
    } finally {
      setStatsLoading(false);
    }
  }, [isAuthenticated]);

  // ============================================
  // TRANSACTIONS
  // ============================================
  const fetchTransactions = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setTransactionsLoading(true);
      const token = localStorage.getItem('authToken');
      const data = await fetchTransactionsList(token);

      let transactionsArray = Array.isArray(data) ? data : [];

      const formattedTransactions = transactionsArray.slice(0, 5).map(tx => {
        let amount = Math.abs(parseFloat(tx.amount || tx.montant || 0));
        let type = tx.type || tx.transaction_type || 'unknown';

        if (type === 'unknown') {
          const originalAmount = parseFloat(tx.amount || tx.montant || 0);
          type = originalAmount < 0 ? 'sms' : 'recharge';
        }

        return {
          id: tx.id || tx.transaction_id,
          amount: amount,
          status: tx.status || tx.statut || 'completed',
          date: tx.date_trans,
          type: type
        };
      });

      setTransactions(formattedTransactions);
    } catch (err) {
      console.error("❌ Erreur transactions:", err);
      setTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  }, [isAuthenticated]);

  // ============================================
  // SENDERS
  // ============================================
  const fetchSenders = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setSendersLoading(true);
      const token = localStorage.getItem('authToken');
      const data = await fetchSenderList(token);

      const sendersArray = Array.isArray(data) ? data : [];
      const formattedSenders = sendersArray.slice(0, 3).map(sender => ({
        id: sender.id,
        name: sender.sender_id || sender.name || sender.sender,
        status: sender.status || (sender.is_active ? 'active' : 'inactive'),
        is_active: sender.is_active || sender.status === 'active'
      }));

      setSenders(formattedSenders);
    } catch (err) {
      console.error("❌ Erreur senders:", err);
      setSenders([]);
    } finally {
      setSendersLoading(false);
    }
  }, [isAuthenticated]);

  // ============================================
  // PRICING
  // ============================================
  const fetchPricing = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setPricingLoading(true);
      const token = localStorage.getItem('authToken');
      const data = await fetchPricingList(token);

      const pricingArray = Array.isArray(data) ? data : [];
      const formattedPricing = pricingArray.slice(0, 3).map(price => ({
        id: price.id,
        country: price.country || 'Standard',
        network: price.network || 'Tous réseaux',
        price: price.sms_pricing || price.price || 0,
        currency: price.currency || 'FCFA'
      }));

      setPricing(formattedPricing);
    } catch (err) {
      console.error("❌ Erreur pricing:", err);
      setPricing([]);
    } finally {
      setPricingLoading(false);
    }
  }, [isAuthenticated]);

  // ============================================
  // SMS RÉCENTS
  // ============================================
  const fetchRecentSMS = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setSmsLoading(true);
      const token = localStorage.getItem('authToken');
      const data = await fetchSMSList(token);

      const smsArray = Array.isArray(data) ? data : [];
      const formattedSMS = smsArray.slice(0, 5).map(sms => ({
        id: sms.id,
        message: sms.message || sms.content,
        recipient: sms.msisdn || sms.phone_number,
        status: sms.sms_status || sms.status,
        date: sms.send_date || sms.created_at,
        cost: (sms.sms_price || 0) * (sms.nb_sms || 1)
      }));

      setRecentSMS(formattedSMS);
    } catch (err) {
      console.error("❌ Erreur SMS récents:", err);
      setRecentSMS([]);
    } finally {
      setSmsLoading(false);
    }
  }, [isAuthenticated]);

  // ============================================
  // ✅ CHARGEMENT INITIAL PROTÉGÉ
  // ============================================
  useEffect(() => {
    if (!isAuthenticated || initDoneRef.current) return;
    initDoneRef.current = true;

    fetchSolde();
    fetchStats();
    fetchTransactions();
    fetchSenders();
    fetchPricing();
    fetchRecentSMS();
  }, [isAuthenticated, fetchSolde, fetchStats, fetchTransactions, fetchSenders, fetchPricing, fetchRecentSMS]);

  // ============================================
  // UTILITAIRES
  // ============================================
  const parseNumberSafely = (value, defaultValue = 0) => {
    try {
      if (value === null || value === undefined || value === '') return defaultValue;
      const number = Number(value);
      if (isNaN(number)) return defaultValue;
      return Math.max(0, Math.floor(number));
    } catch {
      return defaultValue;
    }
  };

  const calculateStatsSummary = () => {
    if (!stats.daily.length && !stats.monthly.length) {
      return {
        yesterday: { total: 0, total_cout: 0 },
        currentMonth: { total: 0, total_cout: 0 },
        lastMonth: { total: 0, total_cout: 0 }
      };
    }

    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayFormatted = yesterday.toISOString().split('T')[0];

    const yesterdayStats = stats.daily.find(day => day.day === yesterdayFormatted) || { total: 0, total_cout: 0 };
    const currentMonthStats = stats.monthly.find(month => month.year === currentYear && month.month === currentMonth) || { total: 0, total_cout: 0 };
    const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const lastMonthYear = currentMonth === 1 ? currentYear - 1 : currentYear;
    const lastMonthStats = stats.monthly.find(month => month.year === lastMonthYear && month.month === lastMonth) || { total: 0, total_cout: 0 };

    return {
      yesterday: {
        total: parseNumberSafely(yesterdayStats.total),
        total_cout: parseNumberSafely(yesterdayStats.total_cout)
      },
      currentMonth: {
        total: parseNumberSafely(currentMonthStats.total),
        total_cout: parseNumberSafely(currentMonthStats.total_cout)
      },
      lastMonth: {
        total: parseNumberSafely(lastMonthStats.total),
        total_cout: parseNumberSafely(lastMonthStats.total_cout)
      }
    };
  };

  const formatDateSafely = (dateString) => {
    try {
      if (!dateString) return 'Date invalide';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Date invalide';
      return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch {
      return 'Date invalide';
    }
  };

  const formatMonthSafely = (year, month) => {
    try {
      const date = new Date(year, month - 1);
      return date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
    } catch {
      return 'Date invalide';
    }
  };

  const formatTransactionDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return "Aujourd'hui";
      if (diffDays === 1) return "Hier";
      if (diffDays < 7) return `Il y a ${diffDays} jours`;
      return date.toLocaleDateString('fr-FR');
    } catch {
      return 'Date inconnue';
    }
  };

  const prepareDailyChartData = () => {
    if (!stats.daily || stats.daily.length === 0) return null;
    const last7Days = [...stats.daily].sort((a, b) => new Date(b.day) - new Date(a.day)).slice(0, 7).reverse();
    return {
      labels: last7Days.map(day => formatDateSafely(day.day)),
      datasets: [
        {
          label: "SMS Envoyés",
          data: last7Days.map(day => parseNumberSafely(day.total)),
          backgroundColor: "#3b82f6",
          borderRadius: 6,
          borderSkipped: false,
        },
        {
          label: "Coût (FCFA)",
          data: last7Days.map(day => parseNumberSafely(day.total_cout)),
          backgroundColor: "#10b981",
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    };
  };

  const prepareMonthlyChartData = () => {
    if (!stats.monthly || stats.monthly.length === 0) return null;
    const last12Months = [...stats.monthly].sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    }).slice(0, 12).reverse();
    return {
      labels: last12Months.map(month => formatMonthSafely(month.year, month.month)),
      datasets: [
        {
          label: "SMS Envoyés",
          data: last12Months.map(month => parseNumberSafely(month.total)),
          backgroundColor: "#3b82f6",
          borderRadius: 6,
          borderSkipped: false,
        },
        {
          label: "Coût (FCFA)",
          data: last12Months.map(month => parseNumberSafely(month.total_cout)),
          backgroundColor: "#10b981",
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    };
  };

  const optionBar = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          font: { size: 12, weight: '600', family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
          color: '#64748b',
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 16,
        }
      },
      tooltip: {
        backgroundColor: '#0f172a',
        titleFont: { size: 12, weight: '600' },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        displayColors: true,
        usePointStyle: true,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: "#94a3b8", font: { size: 11, weight: '500' }, padding: 8 },
        grid: { color: "rgba(148, 163, 184, 0.1)", drawBorder: false }
      },
      x: {
        ticks: { color: "#94a3b8", font: { size: 11, weight: '500' }, padding: 8 },
        grid: { display: false }
      },
    },
  };

  const formatSolde = (amount) => new Intl.NumberFormat('fr-FR').format(parseNumberSafely(amount));
  const formatCurrency = (amount) => new Intl.NumberFormat('fr-FR').format(parseNumberSafely(amount));

  const getUserDataSafely = () => {
    try {
      const userDataStr = localStorage.getItem("userData");
      if (!userDataStr || userDataStr === 'undefined' || userDataStr === 'null') return null;
      return JSON.parse(userDataStr);
    } catch {
      return null;
    }
  };

  const getUserDataSafely1 = () => {
    try {
      const userDataStr1 = localStorage.getItem("businessData");
      if (!userDataStr1 || userDataStr1 === 'undefined' || userDataStr1 === 'null') return null;
      return JSON.parse(userDataStr1);
    } catch {
      return null;
    }
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Bonjour';
    if (h < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };

  if (authLoading) {
    return (
      <div className="dash main main-app p-3 p-lg-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" variant="primary" />
          <span className="ms-3 text-muted">Vérification de l'authentification...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || redirecting) {
    return (
      <div className="dash main main-app p-3 p-lg-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" variant="primary" />
          <span className="ms-3 text-muted">Redirection vers la page de connexion...</span>
        </div>
      </div>
    );
  }

  const statsSummary = calculateStatsSummary();
  const dataBarDay = prepareDailyChartData();
  const dataBarMonth = prepareMonthlyChartData();
  const userData = getUserDataSafely();
  const userData1 = getUserDataSafely1();

  const handleRefreshAll = () => {
    fetchSolde();
    fetchStats();
    fetchTransactions();
    fetchSenders();
    fetchPricing();
    fetchRecentSMS();
  };

  return (
    <div className="dash main main-app p-3 p-lg-4">
      {/* ========== HEADER ========== */}
      <div className="dash-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="dash-header-icon">
              <i className="ri-dashboard-3-line"></i>
            </div>
            <div>
              <h3 className="dash-header-title mb-1">
                {getGreeting()}, {user?.firstname || userData?.firstname || 'Utilisateur'} 
              </h3>
              <p className="dash-header-subtitle mb-0">
                Connecté en tant que <strong>{user?.email || userData?.email || 'Utilisateur'}</strong>
              </p>
            </div>
          </div>
          <Button className="dash-btn-refresh" onClick={handleRefreshAll}>
            <i className="ri-refresh-line"></i>
            <span>Tout actualiser</span>
          </Button>
        </div>
      </div>

      {/* ========== ALERTE KYC ========== */}
      {userData1?.kyc === 'KO' && (
        <div className="dash-alert dash-alert-warning mb-4">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="dash-alert-icon">
              <i className="ri-shield-check-line"></i>
            </div>
            <div className="flex-grow-1">
              <div className="dash-alert-title">Votre compte n'est pas encore validé</div>
              <div className="dash-alert-text">Validez vos informations pour débloquer l'envoi de SMS.</div>
              <div className="dash-alert-progress">
                <div className="dash-alert-progress-bar" style={{ width: '50%' }}></div>
              </div>
            </div>
            <Button as={Link} to="/apps/MonCompte" className="dash-alert-btn">
              <i className="ri-lock-unlock-fill me-1"></i>
              Déverrouiller
            </Button>
          </div>
        </div>
      )}

      {/* ========== LIGNE 1 : SOLDE + CONSOMMATION + ENVOI ========== */}
      <Row className="g-3 mb-4">
        {/* SOLDE */}
        <Col lg={3} md={6}>
          <div className="dash-card dash-card-balance">
            <div className="dash-card-header">
              <div className="d-flex align-items-center gap-2">
                <div className="dash-card-icon-wrapper dash-card-icon-primary">
                  <i className="ri-wallet-3-fill"></i>
                </div>
                <div>
                  <div className="dash-card-title">Mon solde</div>
                  <div className="dash-card-subtitle">Crédits disponibles</div>
                </div>
              </div>
              <button
                className="dash-card-action"
                onClick={fetchSolde}
                disabled={loading}
                title="Actualiser"
              >
                <i className={`ri-refresh-line ${loading ? 'spinning' : ''}`}></i>
              </button>
            </div>

            <div className="dash-card-body">
              <div className="dash-balance-item">
                <div className="dash-balance-label">
                  <i className="ri-message-3-line"></i>
                  Solde SMS
                </div>
                {loading ? (
                  <div className="dash-balance-loading">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : (
                  <div className="dash-balance-value dash-balance-primary">
                    {formatSolde(solde)}
                  </div>
                )}
              </div>

              <div className="dash-balance-item">
                <div className="dash-balance-label">
                  <i className="ri-coins-line"></i>
                  Solde Crédit
                </div>
                {loading ? (
                  <div className="dash-balance-loading">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : (
                  <div className="dash-balance-value dash-balance-success">
                    {formatSolde(soldeV1)}
                  </div>
                )}
              </div>
            </div>

            <Button as={Link} to="/apps/Recharge" className="dash-btn-primary w-100">
              <i className="ri-add-circle-line me-2"></i>
              Recharger mon compte
            </Button>
          </div>
        </Col>

        {/* CONSOMMATION */}
        <Col lg={3} md={6}>
          <div className="dash-card dash-card-consumption">
            <div className="dash-card-header">
              <div className="d-flex align-items-center gap-2">
                <div className="dash-card-icon-wrapper dash-card-icon-success">
                  <i className="ri-bar-chart-fill"></i>
                </div>
                <div>
                  <div className="dash-card-title">Consommation</div>
                  <div className="dash-card-subtitle">Vos envois récents</div>
                </div>
              </div>
            </div>

            <div className="dash-card-body">
              <div className="dash-consumption-item">
                <div className="dash-consumption-icon">
                  <i className="ri-calendar-line"></i>
                </div>
                <div className="dash-consumption-content">
                  <div className="dash-consumption-label">Hier</div>
                  <div className="dash-consumption-value">
                    {statsLoading ? <Spinner animation="border" size="sm" /> : (statsSummary.yesterday.total || 0).toLocaleString('fr-FR')}
                  </div>
                  <div className="dash-consumption-meta">SMS envoyés</div>
                </div>
              </div>

              <div className="dash-consumption-item dash-consumption-item-highlight">
                <div className="dash-consumption-icon dash-consumption-icon-highlight">
                  <i className="ri-calendar-check-line"></i>
                </div>
                <div className="dash-consumption-content">
                  <div className="dash-consumption-label">Ce mois</div>
                  <div className="dash-consumption-value dash-consumption-value-highlight">
                    {statsLoading ? <Spinner animation="border" size="sm" /> : (statsSummary.currentMonth.total || 0).toLocaleString('fr-FR')}
                  </div>
                  <div className="dash-consumption-meta">SMS envoyés</div>
                </div>
              </div>
            </div>
          </div>
        </Col>

        {/* ENVOYER SMS */}
        <Col lg={6} md={12}>
          <div className="dash-card dash-card-send">
            <div className="dash-card-header">
              <div className="d-flex align-items-center gap-2">
                <div className="dash-card-icon-wrapper dash-card-icon-info">
                  <i className="ri-send-plane-fill"></i>
                </div>
                <div>
                  <div className="dash-card-title">Envoyer un SMS</div>
                  <div className="dash-card-subtitle">Choisissez votre méthode d'envoi</div>
                </div>
              </div>
            </div>

            <div className="dash-card-body dash-send-body">
              <Row className="g-3">
                <Col md={6}>
                  <Link to="/apps/Envoie" className="dash-send-option">
                    <div className="dash-send-option-icon dash-send-option-icon-portal">
                      <i className="ri-window-line"></i>
                    </div>
                    <div className="dash-send-option-content">
                      <div className="dash-send-option-title">Via le portail</div>
                      <div className="dash-send-option-desc">Interface web intuitive</div>
                    </div>
                    <i className="ri-arrow-right-line dash-send-option-arrow"></i>
                  </Link>
                </Col>
                <Col md={6}>
                  <Link to="/apps/regenererToken" className="dash-send-option">
                    <div className="dash-send-option-icon dash-send-option-icon-api">
                      <i className="ri-code-s-slash-line"></i>
                    </div>
                    <div className="dash-send-option-content">
                      <div className="dash-send-option-title">Via l'API</div>
                      <div className="dash-send-option-desc">Intégration développeur</div>
                    </div>
                    <i className="ri-arrow-right-line dash-send-option-arrow"></i>
                  </Link>
                </Col>
              </Row>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== LIGNE 2 : GRAPHIQUES ========== */}
      <Row className="g-3 mb-4">
        <Col md={12}>
          <div className="dash-chart-card">
            <div className="dash-chart-header">
              <div className="d-flex align-items-center gap-2">
                <div className="dash-chart-icon dash-chart-icon-blue">
                  <i className="ri-bar-chart-2-fill"></i>
                </div>
                <div>
                  <h6 className="dash-chart-title">Statistiques d'utilisation</h6>
                  <small className="dash-chart-subtitle">Volume de SMS et coûts associés</small>
                </div>
              </div>
              <div className="dash-chart-toggle">
                <button
                  className={`dash-chart-toggle-btn ${viewMode === "jour" ? "active" : ""}`}
                  onClick={() => setViewMode("jour")}
                >
                  <i className="ri-calendar-line"></i>
                  Par jour
                </button>
                <button
                  className={`dash-chart-toggle-btn ${viewMode === "mois" ? "active" : ""}`}
                  onClick={() => setViewMode("mois")}
                >
                  <i className="ri-calendar-2-line"></i>
                  Par mois
                </button>
              </div>
            </div>

            <div className="dash-chart-body">
              {statsLoading ? (
                <div className="dash-loading">
                  <div className="dash-loading-spinner"></div>
                  <div className="dash-loading-text">Chargement des statistiques...</div>
                </div>
              ) : statsError ? (
                <div className="dash-empty-state">
                  <i className="ri-bar-chart-line"></i>
                  <p>{statsError}</p>
                </div>
              ) : (
                <div className="position-relative" style={{ height: "350px" }}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={viewMode}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className="h-100"
                    >
                      {viewMode === "jour" && dataBarDay ? (
                        <Bar data={dataBarDay} options={optionBar} />
                      ) : viewMode === "mois" && dataBarMonth ? (
                        <Bar data={dataBarMonth} options={optionBar} />
                      ) : (
                        <div className="dash-empty-state">
                          <i className="ri-bar-chart-line"></i>
                          <p>Aucune donnée disponible</p>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== LIGNE 3 : TRANSACTIONS + SMS RÉCENTS ========== */}
      <Row className="g-3 mb-4">
        {/* TRANSACTIONS */}
        <Col md={6}>
          <div className="dash-list-card">
            <div className="dash-list-header">
              <div className="d-flex align-items-center gap-2">
                <div className="dash-list-icon dash-list-icon-primary">
                  <i className="ri-history-line"></i>
                </div>
                <div>
                  <h6 className="dash-list-title">Transactions récentes</h6>
                  <small className="dash-list-subtitle">Vos derniers mouvements</small>
                </div>
              </div>
              <Link to="/apps/HistoriquePaiement" className="dash-list-link">
                Voir tout
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="dash-list-body">
              {transactionsLoading ? (
                <div className="dash-loading-small">
                  <div className="dash-loading-spinner-small"></div>
                  <span>Chargement...</span>
                </div>
              ) : transactions.length === 0 ? (
                <div className="dash-empty-small">
                  <i className="ri-wallet-line"></i>
                  <p>Aucune transaction récente</p>
                </div>
              ) : (
                <div className="dash-tx-list">
                  {transactions.map((tx, index) => (
                    <motion.div
                      key={tx.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="dash-tx-item"
                    >
                      <div className={`dash-tx-icon ${tx.type === 'recharge' ? 'dash-tx-icon-success' : 'dash-tx-icon-danger'}`}>
                        <i className={tx.type === 'recharge' ? 'ri-add-line' : 'ri-subtract-line'}></i>
                      </div>
                      <div className="dash-tx-content">
                        <div className="dash-tx-title">
                          {tx.type === 'recharge' ? 'Recharge' : tx.type === 'sms' ? 'Envoi SMS' : 'Transaction'}
                          {tx.status === 'completed' && (
                            <i className="ri-checkbox-circle-fill text-success ms-1"></i>
                          )}
                        </div>
                        <div className="dash-tx-meta">
                          <i className="ri-calendar-line"></i>
                          {formatTransactionDate(tx.date)}
                        </div>
                      </div>
                      <div className="dash-tx-amount">
                        <div className={`dash-tx-value ${tx.type === 'recharge' ? 'text-success' : 'text-danger'}`}>
                          {tx.type === 'recharge' ? '+' : '−'}{tx.amount.toLocaleString('fr-FR')} FCFA
                        </div>
                        <span className={`dash-tx-status ${tx.status === 'completed' ? 'success' : 'warning'}`}>
                          {tx.status === 'completed' ? 'Confirmé' : 'En attente'}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Col>

        {/* SMS RÉCENTS */}
        <Col md={6}>
          <div className="dash-list-card">
            <div className="dash-list-header">
              <div className="d-flex align-items-center gap-2">
                <div className="dash-list-icon dash-list-icon-info">
                  <i className="ri-mail-send-line"></i>
                </div>
                <div>
                  <h6 className="dash-list-title">Derniers SMS envoyés</h6>
                  <small className="dash-list-subtitle">Vos 5 derniers envois</small>
                </div>
              </div>
              <Link to="/apps/HistoriqueMessages" className="dash-list-link">
                Voir tout
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="dash-list-body">
              {smsLoading ? (
                <div className="dash-loading-small">
                  <div className="dash-loading-spinner-small"></div>
                  <span>Chargement...</span>
                </div>
              ) : recentSMS.length === 0 ? (
                <div className="dash-empty-small">
                  <i className="ri-mail-line"></i>
                  <p>Aucun SMS récent</p>
                </div>
              ) : (
                <div className="dash-sms-list">
                  {recentSMS.map((sms) => {
                    const isSent = sms.status === '200' || sms.status === 'Reçu' || sms.status === 'SENT';
                    return (
                      <div key={sms.id} className="dash-sms-item">
                        <div className="dash-sms-header">
                          <div className="dash-sms-recipient">
                            <i className="ri-phone-line"></i>
                            {sms.recipient}
                          </div>
                          <span className={`dash-sms-status ${isSent ? 'success' : 'secondary'}`}>
                            <i className={`ri-${isSent ? 'check' : 'time'}-line`}></i>
                            {isSent ? 'Envoyé' : 'En attente'}
                          </span>
                        </div>
                        <div className="dash-sms-message">
                          {sms.message?.length > 60 ? sms.message.substring(0, 60) + '...' : sms.message}
                        </div>
                        <div className="dash-sms-meta">
                          <i className="ri-calendar-line"></i>
                          {formatTransactionDate(sms.date)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== LIGNE 4 : SENDERS + TARIFS + RÉSUMÉ ========== */}
      <Row className="g-3 mb-4">
        {/* SENDERS */}
        <Col lg={4} md={6}>
          <div className="dash-list-card">
            <div className="dash-list-header">
              <div className="d-flex align-items-center gap-2">
                <div className="dash-list-icon dash-list-icon-violet">
                  <i className="ri-user-settings-line"></i>
                </div>
                <div>
                  <h6 className="dash-list-title">Sender IDs</h6>
                  <small className="dash-list-subtitle">Identifiants actifs</small>
                </div>
              </div>
              <Link to="/apps/SenderID" className="dash-list-link">
                Gérer
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="dash-list-body">
              {sendersLoading ? (
                <div className="dash-loading-small">
                  <div className="dash-loading-spinner-small"></div>
                  <span>Chargement...</span>
                </div>
              ) : senders.length === 0 ? (
                <div className="dash-empty-small">
                  <i className="ri-user-settings-line"></i>
                  <p>Aucun Sender ID configuré</p>
                </div>
              ) : (
                <div className="dash-simple-list">
                  {senders.map((sender) => (
                    <div key={sender.id} className="dash-simple-item">
                      <div className="dash-simple-content">
                        <div className="dash-simple-title">{sender.name}</div>
                        <div className="dash-simple-meta">ID: {sender.id}</div>
                      </div>
                      <span className={`dash-simple-badge ${sender.is_active ? 'success' : 'secondary'}`}>
                        {sender.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Col>

        {/* TARIFS */}
        <Col lg={4} md={6}>
          <div className="dash-list-card">
            <div className="dash-list-header">
              <div className="d-flex align-items-center gap-2">
                <div className="dash-list-icon dash-list-icon-amber">
                  <i className="ri-price-tag-3-line"></i>
                </div>
                <div>
                  <h6 className="dash-list-title">Tarifs SMS</h6>
                  <small className="dash-list-subtitle">Prix par destination</small>
                </div>
              </div>
              <Link to="/apps/Tarifs" className="dash-list-link">
                Voir tout
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="dash-list-body">
              {pricingLoading ? (
                <div className="dash-loading-small">
                  <div className="dash-loading-spinner-small"></div>
                  <span>Chargement...</span>
                </div>
              ) : pricing.length === 0 ? (
                <div className="dash-empty-small">
                  <i className="ri-price-tag-3-line"></i>
                  <p>Aucun tarif disponible</p>
                </div>
              ) : (
                <div className="dash-simple-list">
                  {pricing.map((price) => (
                    <div key={price.id} className="dash-simple-item">
                      <div className="dash-simple-content">
                        <div className="dash-simple-title">{price.country}</div>
                        <div className="dash-simple-meta">{price.network}</div>
                      </div>
                      <div className="dash-simple-value">
                        {price.price} {price.currency}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Col>

        {/* RÉSUMÉ DU JOUR */}
        <Col lg={4} md={12}>
          <div className="dash-summary-card">
            <div className="dash-summary-header">
              <div className="d-flex align-items-center gap-2">
                <div className="dash-summary-icon">
                  <i className="ri-flashlight-fill"></i>
                </div>
                <div>
                  <h6 className="dash-summary-title">Résumé du jour</h6>
                  <small className="dash-summary-subtitle">
                    {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </small>
                </div>
              </div>
            </div>

            <div className="dash-summary-body">
              <div className="dash-summary-stat">
                <div className="dash-summary-stat-icon dash-summary-stat-icon-blue">
                  <i className="ri-send-plane-line"></i>
                </div>
                <div className="dash-summary-stat-content">
                  <div className="dash-summary-stat-label">SMS envoyés</div>
                  <div className="dash-summary-stat-value">
                    {(stats.daily.find(d => d.day === new Date().toISOString().split('T')[0])?.total || 0).toLocaleString('fr-FR')}
                  </div>
                </div>
              </div>

              <div className="dash-summary-stat">
                <div className="dash-summary-stat-icon dash-summary-stat-icon-danger">
                  <i className="ri-money-euro-circle-line"></i>
                </div>
                <div className="dash-summary-stat-content">
                  <div className="dash-summary-stat-label">Coût total</div>
                  <div className="dash-summary-stat-value">
                    {formatCurrency(stats.daily.find(d => d.day === new Date().toISOString().split('T')[0])?.total_cout || 0)} <span className="dash-summary-stat-unit">FCFA</span>
                  </div>
                </div>
              </div>

              <div className="dash-summary-status">
                <div className="dash-summary-status-label">Statut du compte</div>
                <span className={`dash-summary-status-badge ${solde > 1000 ? 'success' : 'warning'}`}>
                  <i className={`ri-${solde > 1000 ? 'shield-check' : 'alert'}-fill`}></i>
                  {solde > 1000 ? 'Crédit suffisant' : 'Crédit faible'}
                </span>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .dash {
          background: #f8fafc;
          min-height: 100vh;
        }
        .dash * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .dash-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .dash-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .dash-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .dash-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }
        .dash-btn-refresh {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          transition: all 0.2s;
        }
        .dash-btn-refresh:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(59, 130, 246, 0.4);
        }

        /* ========== ALERT ========== */
        .dash-alert {
          border-radius: 12px;
          padding: 20px 24px;
          border-left: 4px solid;
        }
        .dash-alert-warning {
          background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
          border-left-color: #f59e0b;
        }
        .dash-alert-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: #f59e0b;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          flex-shrink: 0;
        }
        .dash-alert-title {
          font-weight: 700;
          color: #78350f;
          font-size: 0.95rem;
          margin-bottom: 4px;
        }
        .dash-alert-text {
          color: #92400e;
          font-size: 0.82rem;
          margin-bottom: 10px;
        }
        .dash-alert-progress {
          height: 5px;
          background: rgba(245, 158, 11, 0.2);
          border-radius: 10px;
          overflow: hidden;
        }
        .dash-alert-progress-bar {
          height: 100%;
          background: #f59e0b;
          border-radius: 10px;
          transition: width 0.8s ease;
        }
        .dash-alert-btn {
          background: #f59e0b;
          color: white;
          border: none;
          border-radius: 10px;
          padding: 10px 20px;
          font-weight: 600;
          font-size: 0.875rem;
          display: inline-flex;
          align-items: center;
          flex-shrink: 0;
        }
        .dash-alert-btn:hover {
          background: #d97706;
          color: white;
        }

        /* ========== CARDS ========== */
        .dash-card {
          background: white;
          border-radius: 16px;
          padding: 22px;
          border: 1px solid #e2e8f0;
          transition: all 0.25s;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .dash-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 24px -8px rgba(0,0,0,0.08);
        }
        .dash-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
          gap: 12px;
        }
        .dash-card-icon-wrapper {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .dash-card-icon-primary { background: #eff6ff; color: #3b82f6; }
        .dash-card-icon-success { background: #d1fae5; color: #10b981; }
        .dash-card-icon-info    { background: #cffafe; color: #06b6d4; }
        .dash-card-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.95rem;
          margin-bottom: 2px;
        }
        .dash-card-subtitle {
          color: #94a3b8;
          font-size: 0.75rem;
        }
        .dash-card-body {
          flex-grow: 1;
        }
        .dash-card-action {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.9rem;
        }
        .dash-card-action:hover {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        /* Balance */
        .dash-balance-item {
          padding: 14px 0;
          border-bottom: 1px solid #f1f5f9;
        }
        .dash-balance-item:last-of-type {
          border-bottom: none;
        }
        .dash-balance-label {
          font-size: 0.75rem;
          color: #64748b;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 6px;
        }
        .dash-balance-value {
          font-size: 1.6rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }
        .dash-balance-primary { color: #3b82f6; }
        .dash-balance-success { color: #10b981; }
        .dash-balance-loading {
          padding: 8px 0;
        }

        /* Consumption */
        .dash-consumption-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          border-radius: 12px;
          background: #f8fafc;
          margin-bottom: 10px;
        }
        .dash-consumption-item:last-child {
          margin-bottom: 0;
        }
        .dash-consumption-item-highlight {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
        }
        .dash-consumption-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: white;
          color: #3b82f6;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .dash-consumption-icon-highlight {
          background: #10b981;
          color: white;
        }
        .dash-consumption-content {
          flex-grow: 1;
        }
        .dash-consumption-label {
          font-size: 0.72rem;
          color: #64748b;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 2px;
        }
        .dash-consumption-value {
          font-size: 1.4rem;
          font-weight: 700;
          color: #0f172a;
          line-height: 1.1;
        }
        .dash-consumption-value-highlight {
          color: #059669;
        }
        .dash-consumption-meta {
          font-size: 0.72rem;
          color: #94a3b8;
          margin-top: 2px;
        }

        /* Send options */
        .dash-send-body {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .dash-send-option {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px;
          border-radius: 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          text-decoration: none;
          transition: all 0.2s;
          position: relative;
          overflow: hidden;
          height: 100%;
        }
        .dash-send-option:hover {
          background: white;
          border-color: #3b82f6;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.25);
        }
        .dash-send-option-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          flex-shrink: 0;
        }
        .dash-send-option-icon-portal {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        .dash-send-option-icon-api {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }
        .dash-send-option-content {
          flex-grow: 1;
        }
        .dash-send-option-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.9rem;
          margin-bottom: 2px;
        }
        .dash-send-option-desc {
          font-size: 0.75rem;
          color: #64748b;
        }
        .dash-send-option-arrow {
          color: #cbd5e1;
          font-size: 1.2rem;
          transition: all 0.2s;
        }
        .dash-send-option:hover .dash-send-option-arrow {
          color: #3b82f6;
          transform: translateX(4px);
        }

        /* ========== CHART ========== */
        .dash-chart-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .dash-chart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #f1f5f9;
          background: linear-gradient(180deg, #ffffff, #fafbfc);
          flex-wrap: wrap;
          gap: 12px;
        }
        .dash-chart-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }
        .dash-chart-icon-blue { background: #eff6ff; color: #3b82f6; }
        .dash-chart-title {
          font-weight: 700;
          color: #0f172a;
          margin: 0;
          font-size: 0.95rem;
        }
        .dash-chart-subtitle {
          color: #94a3b8;
          font-size: 0.75rem;
        }
        .dash-chart-toggle {
          display: inline-flex;
          background: #f1f5f9;
          border-radius: 10px;
          padding: 4px;
          gap: 4px;
        }
        .dash-chart-toggle-btn {
          padding: 8px 16px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: #64748b;
          font-weight: 600;
          font-size: 0.8rem;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .dash-chart-toggle-btn:hover {
          color: #3b82f6;
        }
        .dash-chart-toggle-btn.active {
          background: white;
          color: #3b82f6;
          box-shadow: 0 2px 6px rgba(0,0,0,0.06);
        }
        .dash-chart-body {
          padding: 20px 24px;
        }

        /* ========== LIST CARDS ========== */
        .dash-list-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          height: 100%;
          display: flex;
          flex-direction: column;
          transition: all 0.25s;
        }
        .dash-list-card:hover {
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.08);
          border-color: #cbd5e1;
        }
        .dash-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 18px 22px;
          border-bottom: 1px solid #f1f5f9;
          background: linear-gradient(180deg, #ffffff, #fafbfc);
        }
        .dash-list-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }
        .dash-list-icon-primary { background: #eff6ff; color: #3b82f6; }
        .dash-list-icon-info    { background: #cffafe; color: #06b6d4; }
        .dash-list-icon-violet  { background: #ede9fe; color: #8b5cf6; }
        .dash-list-icon-amber   { background: #fef3c7; color: #f59e0b; }
        .dash-list-title {
          font-weight: 700;
          color: #0f172a;
          margin: 0;
          font-size: 0.9rem;
        }
        .dash-list-subtitle {
          color: #94a3b8;
          font-size: 0.72rem;
        }
        .dash-list-link {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          color: #3b82f6;
          font-weight: 600;
          font-size: 0.78rem;
          text-decoration: none;
          transition: all 0.2s;
        }
        .dash-list-link:hover {
          color: #2563eb;
          transform: translateX(2px);
        }
        .dash-list-body {
          flex-grow: 1;
          padding: 8px 0;
          min-height: 200px;
        }

        /* Transactions */
        .dash-tx-list {
          padding: 0 8px;
        }
        .dash-tx-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          border-radius: 10px;
          transition: all 0.2s;
          cursor: pointer;
        }
        .dash-tx-item:hover {
          background: #f8fafc;
        }
        .dash-tx-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .dash-tx-icon-success { background: #d1fae5; color: #10b981; }
        .dash-tx-icon-danger  { background: #fee2e2; color: #ef4444; }
        .dash-tx-content {
          flex-grow: 1;
          min-width: 0;
        }
        .dash-tx-title {
          font-weight: 600;
          color: #0f172a;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
        }
        .dash-tx-meta {
          font-size: 0.72rem;
          color: #94a3b8;
          margin-top: 3px;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .dash-tx-amount {
          text-align: right;
          flex-shrink: 0;
        }
        .dash-tx-value {
          font-weight: 700;
          font-size: 0.9rem;
          white-space: nowrap;
        }
        .dash-tx-status {
          display: inline-block;
          font-size: 0.65rem;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 10px;
          margin-top: 4px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .dash-tx-status.success { background: #d1fae5; color: #059669; }
        .dash-tx-status.warning { background: #fef3c7; color: #d97706; }

        /* SMS list */
        .dash-sms-list {
          padding: 0 8px;
        }
        .dash-sms-item {
          padding: 12px 14px;
          border-radius: 10px;
          transition: background 0.2s;
        }
        .dash-sms-item:hover {
          background: #f8fafc;
        }
        .dash-sms-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
          gap: 8px;
        }
        .dash-sms-recipient {
          font-size: 0.8rem;
          color: #64748b;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .dash-sms-status {
          font-size: 0.65rem;
          font-weight: 700;
          padding: 3px 9px;
          border-radius: 10px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
        }
        .dash-sms-status.success { background: #d1fae5; color: #059669; }
        .dash-sms-status.secondary { background: #f1f5f9; color: #64748b; }
        .dash-sms-message {
          font-size: 0.82rem;
          color: #334155;
          line-height: 1.4;
          margin-bottom: 6px;
        }
        .dash-sms-meta {
          font-size: 0.72rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        /* Simple list (senders, pricing) */
        .dash-simple-list {
          padding: 0 8px;
        }
        .dash-simple-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 14px;
          border-radius: 10px;
          transition: background 0.2s;
        }
        .dash-simple-item:hover {
          background: #f8fafc;
        }
        .dash-simple-content {
          min-width: 0;
          flex-grow: 1;
        }
        .dash-simple-title {
          font-weight: 600;
          color: #0f172a;
          font-size: 0.85rem;
          margin-bottom: 2px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .dash-simple-meta {
          font-size: 0.72rem;
          color: #94a3b8;
        }
        .dash-simple-badge {
          font-size: 0.65rem;
          font-weight: 700;
          padding: 3px 9px;
          border-radius: 10px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .dash-simple-badge.success { background: #d1fae5; color: #059669; }
        .dash-simple-badge.secondary { background: #f1f5f9; color: #64748b; }
        .dash-simple-value {
          font-weight: 700;
          color: #3b82f6;
          font-size: 0.9rem;
          white-space: nowrap;
          flex-shrink: 0;
        }

        /* Summary card */
        .dash-summary-card {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          border-radius: 16px;
          padding: 22px;
          color: white;
          height: 100%;
          position: relative;
          overflow: hidden;
        }
        .dash-summary-card::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%);
          pointer-events: none;
        }
        .dash-summary-header {
          margin-bottom: 20px;
          position: relative;
          z-index: 1;
        }
        .dash-summary-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }
        .dash-summary-title {
          font-weight: 700;
          color: white;
          margin: 0;
          font-size: 0.9rem;
        }
        .dash-summary-subtitle {
          color: #94a3b8;
          font-size: 0.72rem;
          text-transform: capitalize;
        }
        .dash-summary-body {
          position: relative;
          z-index: 1;
        }
        .dash-summary-stat {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 0;
          border-bottom: 1px solid rgba(148, 163, 184, 0.15);
        }
        .dash-summary-stat:last-of-type {
          border-bottom: none;
        }
        .dash-summary-stat-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .dash-summary-stat-icon-blue   { background: rgba(59, 130, 246, 0.2); color: #60a5fa; }
        .dash-summary-stat-icon-danger { background: rgba(239, 68, 68, 0.2); color: #f87171; }
        .dash-summary-stat-content {
          flex-grow: 1;
          min-width: 0;
        }
        .dash-summary-stat-label {
          font-size: 0.72rem;
          color: #94a3b8;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 3px;
        }
        .dash-summary-stat-value {
          font-size: 1.3rem;
          font-weight: 700;
          color: white;
          line-height: 1.1;
        }
        .dash-summary-stat-unit {
          font-size: 0.75rem;
          font-weight: 600;
          color: #94a3b8;
          margin-left: 4px;
        }
        .dash-summary-status {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid rgba(148, 163, 184, 0.15);
        }
        .dash-summary-status-label {
          font-size: 0.72rem;
          color: #94a3b8;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 8px;
        }
        .dash-summary-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 0.78rem;
          font-weight: 700;
        }
        .dash-summary-status-badge.success {
          background: rgba(16, 185, 129, 0.2);
          color: #34d399;
        }
        .dash-summary-status-badge.warning {
          background: rgba(245, 158, 11, 0.2);
          color: #fbbf24;
        }

        /* ========== LOADING / EMPTY ========== */
        .dash-loading {
          padding: 80px 20px;
          text-align: center;
        }
        .dash-loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        .dash-loading-text {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .dash-loading-small {
          padding: 40px 20px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          color: #94a3b8;
          font-size: 0.8rem;
        }
        .dash-loading-spinner-small {
          width: 24px;
          height: 24px;
          border: 3px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        .dash-empty-state {
          padding: 80px 20px;
          text-align: center;
          color: #94a3b8;
        }
        .dash-empty-state i {
          font-size: 3rem;
          opacity: 0.5;
          display: block;
          margin-bottom: 12px;
        }
        .dash-empty-state p {
          margin: 0;
          font-size: 0.875rem;
          font-weight: 500;
        }
        .dash-empty-small {
          padding: 40px 20px;
          text-align: center;
          color: #94a3b8;
        }
        .dash-empty-small i {
          font-size: 2rem;
          opacity: 0.5;
          display: block;
          margin-bottom: 8px;
        }
        .dash-empty-small p {
          margin: 0;
          font-size: 0.8rem;
          font-weight: 500;
        }

        /* ========== BUTTONS ========== */
        .dash-btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 10px 18px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
          transition: all 0.2s;
          margin-top: 16px;
        }
        .dash-btn-primary:hover {
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 8px 16px -4px rgba(15, 23, 42, 0.4);
        }

        /* ========== ANIMATIONS ========== */
        .spinning {
          animation: spin 1s linear infinite;
          display: inline-block;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .dash-header {
            padding: 20px;
          }
          .dash-header-title {
            font-size: 1.2rem;
          }
          .dash-balance-value {
            font-size: 1.3rem;
          }
          .dash-consumption-value {
            font-size: 1.2rem;
          }
          .dash-alert {
            padding: 16px;
          }
          .dash-alert-icon {
            width: 40px;
            height: 40px;
            font-size: 18px;
          }
          .dash-chart-toggle-btn span {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}